<template>
  <div class="app-page">
    <Header ref="header">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
      <template #page-slot>
        <span v-if="org" class="mt-0 mb-0">{{ org.name }}</span>
      </template>
    </Header>
    <div class="main-page-div">
      <div class="user-router">
        <div class="user-router__container">

          <ul>
            <li :class="{ 'active': currentTab.includes('super/list') }" @click="navigateToOrganizationList">
              <SVGIcon
                :iconName="'organizations'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>Organization List</span>
            </li>
            <li :class="{ 'active': currentTab.includes('super/neworg') }" @click="navigateToCreateOrganization">
              <SVGIcon
                :iconName="'people_add'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>New Organization</span>
            </li>

              <li :class="{ 'active': currentTab.includes('super/coupons') }" @click="navigateToCreateCouponsPage">
              <SVGIcon
                :iconName="'voucher'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>Create Coupons</span>
            </li>

            <li :class="{ 'active': currentTab.includes('super/cost_report') }" @click="navigateToCostReportPage">
              <SVGIcon
                :iconName="'dollar'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>Cost Report</span>
            </li>
            <li
              role="button"
              tabindex="0"
              :aria-disabled="isDownloadingActivityReport"
              @click="downloadActivityReport"
              @keydown.enter.prevent="downloadActivityReport"
              @keydown.space.prevent="downloadActivityReport"
            >
              <SVGIcon
                :iconName="'download'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>{{ isDownloadingActivityReport ? 'Downloading Activity Report...' : 'Activity Report' }}</span>
            </li>
            <hr>

            <span v-if="org">

            <li :class="{ 'active': currentTab.includes('super/users') }" @click="navigateToUsersPage">
              <SVGIcon
                :iconName="'user'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>Users</span>
            </li>
           

            <li :class="{ 'active': currentTab.includes('super/bills') }" @click="navigateToBillsPage">
              <SVGIcon
                :iconName="'usage'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>Bills</span>
            </li>

            <li :class="{ 'active': currentTab.includes('super/transactions') }" @click="navigateToTransactionPage">
              <SVGIcon
                :iconName="'receipt'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>Transactions</span>
            </li>

            <li :class="{ 'active': currentTab.includes('super/bills') }" @click="navigateToUsagePage">
              <SVGIcon
                :iconName="'chart'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>Usage</span>
            </li>

            <li :class="{ 'active': currentTab.includes('super/addcredits') }" @click="navigateToAddFundsPage">
              <SVGIcon
                :iconName="'dollar'"
                :width="'20px'"
                :height="'20px'"
              />
              <span>Add Credits</span>
            </li>

            </span>

          </ul>
        </div>
      </div>
      <main class="main-display-area">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';
import Header from '@/components/Header.vue';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue';
import { useToast } from 'vue-toast-notification';

export default {
  name: 'UserProfile',
  setup() {
    return { toast: useToast() };
  },
  components: {
    Header,
    BreadcrumbNavigation,
    SVGIcon,
  },
  data() {
    return {
      dsConn: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      currentTab: 'account',
      org: null,
      isDownloadingActivityReport: false,
    };
  },
  computed: {
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    user() {
      return this.$store.state.user.user;
    },
    hasOrg() {
      return this.user && this.user.organization_id !== 0;
    },
    isAdmin() {
      return this.user && (this.user.role === 'super' || this.user.role === 'admin');
    },
    isSuper() {
      return this.user && this.user.role === 'super';
    },
  },
  watch: {
    $route(to, from) {
      if (to.query.org) {
        this.getOrg(parseInt(to.query.org));
      } else {
        this.org = null;
      }
      if (to && to !== '') {
        this.currentTab = to.fullPath;
      }
    },
  },
  created() {
    if (!this.isSuper) {
      this.$router.push('/login');
    }
    if (this.$route) {
      this.currentTab = this.$route.fullPath;
    }
  },
  methods: {
    async downloadActivityReport() {
      if (this.isDownloadingActivityReport) return;
      this.isDownloadingActivityReport = true;

      try {
        const response = await this.dsConn.apiBlob('admin.activity.pdf');
        let pdfBlob;

        if (response instanceof Blob && !response.type.includes('json')) {
          pdfBlob = response;
        } else {
          const payload = response instanceof Blob ? JSON.parse(await response.text()) : response;
          const encodedPdf = payload?.result?.pdf
            ?? payload?.result?.data
            ?? payload?.result;
          if (typeof encodedPdf !== 'string') {
            throw new Error(payload?.error?.message || 'The server did not return a PDF.');
          }
          const base64 = encodedPdf.replace(/^data:application\/pdf;base64,/, '');
          const binary = atob(base64);
          const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
          pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        }

        if (await pdfBlob.slice(0, 5).text() !== '%PDF-') {
          throw new Error('The server response is not a valid PDF.');
        }

        const downloadUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `activity-report-${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        this.toast.error(error?.message || 'Unable to download the activity report.', { position: 'top' });
      } finally {
        this.isDownloadingActivityReport = false;
      }
    },
    async getOrg(id) {
      await this.dsConn.getOrg({ organization_id: id })
        .then((data) => {
          if (data.result) {
            this.org = data.result;
          }
        })
        .catch((e) => { this.message = e; });
    },
    navigateToCostReportPage() {
      this.$router.push({ name: 'super.costreport' });
    },    
    navigateToCreateOrganization() {
      this.$router.push({ name: 'super.neworg' });
    },
    navigateToOrganizationList() {
      this.$router.push({ name: 'super.list' });
    },
    navigateToAddCredits() {
      this.$router.push({ name: 'super.addcredits', query: { org: this.org.id } });
    },
    navigateToUserEmulation() {
      this.dsConn.send2FA({ username: this.user.username });
      this.$router.push({ name: 'super.emulate' });
    },
    navigateToCreateCouponsPage() {
      this.$router.push({ name: 'super.coupons' });
    },
    navigateToUsersPage() {
      this.$router.push({ name: 'super.users', query: { org: this.org.id } });
    },
    navigateToUsagePage() {
      this.$router.push({ name: 'super.usage', query: { org: this.org.id } });
    },
    navigateToTransactionPage() {
      this.$router.push({ name: 'super.transactions', query: { org: this.org.id } });
    },
    navigateToBillsPage() {
      this.$router.push({ name: 'super.bills', query: { org: this.org.id } });
    },
    navigateToAddFundsPage() {
      this.$router.push({ name: 'super.addcredits', query: { org: this.org.id } });
    },
  },
};
</script>

<style lang="scss" scoped>

.user-router {
  min-width: 250px;
  width: 250px;
  @include themify() {
    background-color: themed('background-light-gray');
  };
  padding: 30px 0px 30px 30px;

  &__container {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 100%;
    padding-right: 30px;

    ul {
      list-style-type: none;
    }

    ul + ul {
      margin-top: 20px;
    }

    li {
      display: flex;
      flex-direction: row;
      margin-top: 2px;
      margin-bottom: 2px;
      font-size: 0.9rem;
      padding: 4px;
      color: $color-link;
      cursor: pointer;
      border-radius: 4px;

      span {
        margin-left: 5px;
      }

      &.secondary {
          margin-left: 15px;
      }
    }

    li:hover {
      background-color: rgba(140, 140, 140, 0.1);

    }

    li:focus-visible {
      background-color: rgba(140, 140, 140, 0.1);
    }

    li.active {
      background-color: rgba(140, 140, 140, 0.2);
      color: inherit;
    }
  }
}
</style>
